import { Draft } from '@reduxjs/toolkit'
import { filter, find, forEach, get, isEmpty, map, mapValues, pickBy } from 'lodash'

import { CommentObject, LabelingObjectData, LabelingSnapshot, LabelingState } from '@/stores/slices/Labeling/types'
import { getSettledFiles, transformAnnotObjects } from '@/stores/slices/Labeling/utils'

import { LabelingNodeItem, LabelingObject, LabelingStatus } from '@/api/LabelingNode/types'
import { LABELING_STATUS } from '@/api/LabelingNode/const'

/**
 * 해당 라벨링아이템의 status, numOfLabelObjects 업데이트
 */
export const updateLabelingItemInfo = (state: Draft<LabelingState>, itemId: string, snapshot: LabelingSnapshot) => {
  const files = getSettledFiles(state)
  const fileIndex = files.findIndex((f) => f._id === itemId)

  if (fileIndex !== -1) {
    files[fileIndex].status = snapshot.status
    files[fileIndex].numOfLabelObjects = snapshot.object.length
  }
}

/**
 * 현재 labelingItem의 스냅샷 추가
 */
export const addSnapshot = (state: Draft<LabelingState>, itemId: string, snapshot: LabelingSnapshot) => {
  state.annotation[itemId] = snapshot
  updateLabelingItemInfo(state, itemId, snapshot)
}

/**
 * 해당 라벨링아이템의 오브젝트들을 업데이트
 */
export const updateObjects = (state: Draft<LabelingState>, itemId: string, objects: (LabelingObjectData | undefined)[]) => {
  forEach(objects, (updatedObject) => {
    if (updatedObject) {
      state.annotation[itemId].object = map(state.annotation[itemId].object, (o) => (o.id === updatedObject.id ? updatedObject : o))
    }
  })
}

export const undoRedoObject = (state: Draft<LabelingState>, objectStates: {
  currentObjectsState: LabelingObject[] | null,
  prevObjectState: LabelingObject[]
} | null) => {
  const curLabelingItem = state.files[state.currentIndex]

  if (objectStates) {
    const { currentObjectsState, prevObjectState } = objectStates

    const itemId = get(currentObjectsState, '[0].labelingItemId', '') || prevObjectState[0].labelingItemId

    if (!!itemId && itemId === curLabelingItem?._id) {
      if (currentObjectsState === null) {
        const objectIds = map(prevObjectState, (o) => o._id)
        state.annotation[itemId].object = filter(state.annotation[itemId].object, (o) => !objectIds.includes(o.id || ''))
        return
      }
      forEach(currentObjectsState, (o) => {
        const foundObject = find(state.annotation[itemId].object, { id: o._id })
        const transformedObject = transformAnnotObjects([o])[0]
        if (foundObject) {
          updateObjects(state, itemId, [transformedObject])
        } else {
          state.annotation[itemId].object = [...state.annotation[itemId].object, transformedObject]
        }
      })
    }
  }
}

/**
 * labelingItem의 status 업데이트(annot, files, originalFiles)
 */
export const updateLabelingItemStatus = (state: Draft<LabelingState>, payload: {
  itemId: string
  updatedStatus: LabelingStatus
}) => {
  const { itemId, updatedStatus } = payload

  state.annotation[itemId] = { ...state.annotation[itemId], status: updatedStatus }
  state.files = state.files.map((f) => (f?._id === itemId ? { ...f, status: updatedStatus } : f))
  state.originalFiles = state.originalFiles.map((f) => (f?._id === itemId ? { ...f, status: updatedStatus } : f))
}

/**
 * update LabelAssistant status
 */
export const updateLabelAssistantStatus = (state: Draft<LabelingState>, payload: { itemId: string, isRunning: boolean }) => {
  const { itemId, isRunning } = payload
  state.labelAssistant = { itemId, isRunning }
}

// COMMENT
export const updateComments = (
  state: Draft<LabelingState>,
  files: LabelingNodeItem[] | LabelingNodeItem
) => {
  const convertToCommentObject = (objects: LabelingObject[]) => map(
    filter(objects, (o) => o.type === 'COMMENT'),
    (o) => ({
      _id: o._id,
      labelingItemId: o.labelingItemId,
      isConfirm: o.isConfirm,
      region: o.region,
      comments: o.comments,
      createdAt: o.createdAt,
    } as CommentObject)
  )

  if (Array.isArray(files)) {
    const comments: LabelingState['comments'] = {}

    files.forEach((file) => {
      const labelingItemId = file._id
      if (file.labelingObjects) {
        comments[labelingItemId] = convertToCommentObject(file.labelingObjects)
      }
    })
    state.comments = comments
  } else {
    state.comments = { ...state.comments, [files._id]: convertToCommentObject(files.labelingObjects) }
  }
}

export const createInitialCommentObject = (state: Draft<LabelingState>, newObject: LabelingObject) => {
  const { labelingItemId, _id: labelingObjectId, region, comments, isConfirm, createdAt } = newObject

  if (!state.comments[labelingItemId]) {
    state.comments[labelingItemId] = []
  }

  state.comments[labelingItemId].push({
    _id: labelingObjectId,
    labelingItemId,
    region,
    comments: comments ?? [],
    isConfirm: isConfirm ?? false,
    createdAt,
  })
}

export const updateAppendComment = (state: Draft<LabelingState>, payload: LabelingObject) => {
  const { labelingItemId, _id: labelingObjectId, comments } = payload

  state.comments[labelingItemId] = map(
    state.comments[labelingItemId],
    (o) => (o._id === labelingObjectId
      ? { ...o, comments: comments ?? [] }
      : o)
  )
}

export const updateDeleteComment = (state: Draft<LabelingState>, payload: LabelingObject) => {
  const { labelingItemId, _id: labelingObjectId, comments } = payload

  if (!isEmpty(comments)) {
    state.comments[labelingItemId] = map(state.comments[labelingItemId], (o) => (o._id === labelingObjectId ? { ...o, comments: comments ?? [] } : o))
  } else {
    // 오브젝트 자체도 없애야함
    state.comments[labelingItemId] = filter(state.comments[labelingItemId], (o) => o._id !== labelingObjectId)
  }
}

export const updateToggleConfirmComment = (state: Draft<LabelingState>, payload: LabelingObject) => {
  const { labelingItemId, _id: labelingObjectId, isConfirm } = payload

  state.comments[labelingItemId] = map(
    state.comments[labelingItemId],
    (o) => (o._id === labelingObjectId
      ? { ...o, isConfirm: isConfirm ?? o.isConfirm }
      : o)
  )
}

export const updateConfirmAllComments = (state: Draft<LabelingState>, payload: {
  labelingItemId: string
  shouldCompleteReview: boolean
}) => {
  const { labelingItemId, shouldCompleteReview } = payload

  if (labelingItemId) {
    state.comments[labelingItemId] = map(state.comments[labelingItemId], (o) => ({ ...o, isConfirm: true }))
    if (shouldCompleteReview) {
      state.files = map(state.files, (f) => (f?._id === labelingItemId ? { ...f, status: LABELING_STATUS.COMPLETED } : f))
    }
  } else {
    state.comments = mapValues(state.comments, (v) => map(v, (comment) => ({ ...comment, isConfirm: true })))
    if (shouldCompleteReview) {
      const itemIdsWithComments = map(pickBy(state.comments, (c) => c.length > 0), (v, k) => k)
      forEach(itemIdsWithComments, (id) => {
        if (state.annotation[id]) {
          state.annotation[id] = { ...state.annotation[id], status: LABELING_STATUS.COMPLETED }
        }
      })
      state.files = map(state.files, (f) => (itemIdsWithComments.includes(f?._id ?? '') ? { ...f as LabelingNodeItem, status: LABELING_STATUS.COMPLETED } : f))
      state.originalFiles = map(state.originalFiles, (f) => (itemIdsWithComments.includes(f?._id ?? '') ? ({ ...f as LabelingNodeItem, status: LABELING_STATUS.COMPLETED }) : f))
    }
  }
}
